import React, { useState, useEffect } from 'react';
import { 
  useGetWithdrawalsQuery,
  useUpdateWithdrawalStatusMutation 
} from './../../features/withdrawalApi/withdrawalApi';
import { 
  FiChevronLeft, 
  FiChevronRight, 
  FiLoader, 
  FiAlertCircle, 
  FiCheck, 
  FiX, 
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiClock,
  FiDollarSign,
  FiUser
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO } from 'date-fns';

const statusStyles = {
  pending: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  approved: 'bg-green-50 text-green-800 border-green-200',
  rejected: 'bg-red-50 text-red-800 border-red-200',
};

const statusIcons = {
  pending: <FiClock className="inline mr-1" />,
  approved: <FiCheck className="inline mr-1" />,
  rejected: <FiX className="inline mr-1" />,
};

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const WithdrawalsManagement = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page when search changes
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const queryParams = {
    page,
    limit,
    ...(statusFilter !== 'all' && { status: statusFilter }),
    ...(debouncedSearch && { search: debouncedSearch })
  };
  
  const { 
    data: { withdrawals, meta } = {}, 
    isLoading, 
    isError,
    error,
    refetch
  } = useGetWithdrawalsQuery(queryParams);
  
  const [updateStatus, { isLoading: isUpdating }] = useUpdateWithdrawalStatusMutation();

  const handleStatusUpdate = async (withdrawalId, status) => {
    try {
      await updateStatus({ withdrawalId, status }).unwrap();
      refetch();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleResetFilters = () => {
    setStatusFilter('all');
    setSearchQuery('');
    setPage(1);
  };

  if (isLoading) return (
    <div className="flex justify-center items-center h-64">
      <FiLoader className="animate-spin text-3xl text-blue-500" />
    </div>
  );

  if (isError) return (
    <motion.div 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md mx-auto mt-8 bg-red-50 border-l-4 border-red-500 p-4 rounded shadow"
    >
      <div className="flex items-center">
        <FiAlertCircle className="text-red-500 text-xl mr-2" />
        <div>
          <h3 className="text-red-800 font-medium">Error loading withdrawals</h3>
          <p className="text-red-600 text-sm">{error?.data?.message || error.message}</p>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Withdrawal Requests</h1>
            <p className="text-gray-600 mt-1">Manage and process customer withdrawal requests</p>
          </div>
          <button
            onClick={refetch}
            className="mt-4 md:mt-0 flex items-center px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
          >
            <FiRefreshCw className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0">
            <div className="relative flex-grow">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by name, email, phone or bank details"
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center px-4 py-2 border rounded-lg transition-colors ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                <FiFilter className="mr-2" />
                Filters
              </button>
              
              {(statusFilter !== 'all' || searchQuery) && (
                <button
                  onClick={handleResetFilters}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Expanded Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-4 pt-4 border-t border-gray-200"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(1);
                      }}
                    >
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Items per page</label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={limit}
                      onChange={(e) => {
                        setLimit(Number(e.target.value));
                        setPage(1);
                      }}
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Stats Cards */}
        {meta && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-blue-50 text-blue-600 mr-4">
                  <FiDollarSign size={20} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Requests</p>
                  <p className="text-xl font-semibold">{meta.total}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-yellow-50 text-yellow-600 mr-4">
                  <FiClock size={20} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-xl font-semibold">
                    {withdrawals?.filter(w => w.status === 'pending').length || 0}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center">
                <div className="p-3 rounded-lg bg-green-50 text-green-600 mr-4">
                  <FiUser size={20} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Unique Users</p>
                  <p className="text-xl font-semibold">
                    {new Set(withdrawals?.map(w => w.userId)).size || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Information</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {withdrawals?.map((withdrawal) => (
                  <motion.tr 
                    key={withdrawal.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-blue-600 font-medium">
                            {withdrawal.user?.name?.charAt(0) || 'U'}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {withdrawal.user?.name || 'Unknown User'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {withdrawal.user?.email || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {withdrawal.user?.phone || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-medium">{withdrawal.bankAccount}</div>
                      <div className="text-sm text-gray-500">IFSC: {withdrawal.ifscCode}</div>
                      <div className="text-sm text-gray-500">{withdrawal.branch}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-lg font-semibold text-gray-900">
                        ₹{withdrawal.withdrawalAmount.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${statusStyles[withdrawal.status]}`}>
                        {statusIcons[withdrawal.status]}
                        {withdrawal.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(parseISO(withdrawal.createdAt), 'dd MMM yyyy, hh:mm a')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {withdrawal.status === 'pending' ? (
                        <div className="flex space-x-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleStatusUpdate(withdrawal.id, 'approved')}
                            disabled={isUpdating}
                            className="px-3 py-1 bg-green-50 text-green-700 rounded-lg text-sm hover:bg-green-100 transition-colors flex items-center border border-green-100"
                          >
                            {isUpdating ? <FiLoader className="animate-spin mr-1" /> : <FiCheck className="mr-1" />}
                            Approve
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleStatusUpdate(withdrawal.id, 'rejected')}
                            disabled={isUpdating}
                            className="px-3 py-1 bg-red-50 text-red-700 rounded-lg text-sm hover:bg-red-100 transition-colors flex items-center border border-red-100"
                          >
                            {isUpdating ? <FiLoader className="animate-spin mr-1" /> : <FiX className="mr-1" />}
                            Reject
                          </motion.button>
                        </div>
                      ) : (
                        <div className="text-gray-400">No actions available</div>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {withdrawals?.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-2">No withdrawal requests found</div>
              <div className="text-sm text-gray-500">Try adjusting your search or filter criteria</div>
            </div>
          )}
        </div>

        {/* Pagination */}
        {meta?.pages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between mt-6 px-2 space-y-4 sm:space-y-0">
            <div className="text-sm text-gray-500">
              Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{' '}
              <span className="font-medium">{Math.min(page * limit, meta.total)}</span> of{' '}
              <span className="font-medium">{meta.total}</span> results
            </div>
            
            <div className="flex items-center space-x-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className={`flex items-center px-4 py-2 rounded-lg ${page === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm'}`}
              >
                <FiChevronLeft className="mr-1" />
                Previous
              </motion.button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, meta.pages) }, (_, i) => {
                  const pageNum = page <= 3 
                    ? i + 1 
                    : page >= meta.pages - 2 
                      ? meta.pages - 4 + i 
                      : page - 2 + i;
                  
                  if (pageNum < 1 || pageNum > meta.pages) return null;
                  
                  return (
                    <motion.button
                      key={pageNum}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setPage(pageNum)}
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${page === pageNum ? 'bg-blue-500 text-white shadow-md' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`}
                    >
                      {pageNum}
                    </motion.button>
                  );
                })}
                
                {meta.pages > 5 && page < meta.pages - 2 && (
                  <span className="px-2 text-gray-500">...</span>
                )}
                
                {meta.pages > 5 && page < meta.pages - 2 && (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setPage(meta.pages)}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${page === meta.pages ? 'bg-blue-500 text-white shadow-md' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`}
                  >
                    {meta.pages}
                  </motion.button>
                )}
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setPage(p => Math.min(meta.pages, p + 1))}
                disabled={page === meta.pages}
                className={`flex items-center px-4 py-2 rounded-lg ${page === meta.pages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm'}`}
              >
                Next
                <FiChevronRight className="ml-1" />
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default WithdrawalsManagement;